
import { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Clock, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { FEATURE_FLAGS } from '@/config/features';
import deliveryIcon from '@/assets/driver-delivery-icon.png';
import rideIcon from '@/assets/driver-ride-icon.png';
import rentalIcon from '@/assets/driver-rental-icon.png';

interface DriverApplication {
  id: string;
  phone_number: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  rejection_reason: string | null;
  full_name: string | null;
  email: string | null;
  license_number: string | null;
  vehicle_type: string | null;
  vehicle_color: string | null;
  vehicle_make: string | null;
  driver_type: string | null;
}

const VEHICLE_MAKES = [
  'Acura','Alfa Romeo','Aston Martin','Audi','Bentley','BMW','Buick','Cadillac','Chevrolet','Chrysler',
  'Citroën','Dacia','Daewoo','Daihatsu','Dodge','Ferrari','Fiat','Ford','Genesis','GMC',
  'Honda','Hummer','Hyundai','Infiniti','Isuzu','Jaguar','Jeep','Kia','Lamborghini','Land Rover',
  'Lexus','Lincoln','Lotus','Maserati','Mazda','McLaren','Mercedes-Benz','Mercury','Mini','Mitsubishi',
  'Nissan','Opel','Peugeot','Pontiac','Porsche','Ram','Renault','Rolls-Royce','Saab','Saturn',
  'Scion','Seat','Škoda','Smart','Subaru','Suzuki','Tesla','Toyota','Volkswagen','Volvo',
];

const VEHICLE_COLORS = [
  'Black','White','Silver','Gray','Red','Blue','Dark Blue','Light Blue','Green','Dark Green',
  'Brown','Beige','Gold','Yellow','Orange','Purple','Maroon','Burgundy','Champagne','Bronze',
  'Charcoal','Pearl White','Metallic Silver','Metallic Blue','Metallic Red','Metallic Gray',
];

type DriverType = 'delivery' | 'ride' | 'rental';

const DRIVER_TYPE_CONFIG: Record<DriverType, { label: string; icon: React.ReactNode; description: string; vehicleOptions: { value: string; label: string }[]; requirements: string[] }> = {
  delivery: {
    label: 'Delivery Driver',
    icon: <img src={deliveryIcon} alt="Delivery" className="h-16 w-16 object-contain" />,
    description: 'Deliver food & packages to customers',
    vehicleOptions: [
      { value: 'car', label: 'Car' },
      { value: 'motorbike', label: 'Motorbike' },
    ],
    requirements: [
      "Valid driver's licence",
      'Own vehicle (car or motorbike)',
      'Clean driving record',
      'Must be 18 years or older',
      'Smartphone with data plan',
    ],
  },
  ride: {
    label: 'Ride Driver',
    icon: <img src={rideIcon} alt="Ride" className="h-16 w-16 object-contain" />,
    description: 'Drive passengers around The Gambia',
    vehicleOptions: [
      { value: 'economy', label: 'Economy' },
      { value: 'comfort', label: 'Comfort' },
      { value: 'comfort_plus', label: 'Comfort+' },
      { value: 'comfort_xl', label: 'Comfort XL' },
    ],
    requirements: [
      "Valid driver's licence",
      'Own vehicle in good condition',
      'Clean driving record',
      'Must be 21 years or older',
      'Smartphone with data plan',
      'Vehicle insurance',
    ],
  },
  rental: {
    label: 'Rental Partner',
    icon: <img src={rentalIcon} alt="Rental" className="h-16 w-16 object-contain" />,
    description: 'List your vehicle(s) for rental',
    vehicleOptions: [
      { value: 'sedan', label: 'Sedan' },
      { value: 'suv', label: 'SUV' },
      { value: 'minivan', label: 'Minivan' },
      { value: 'truck', label: 'Truck' },
    ],
    requirements: [
      "Valid driver's licence",
      'Own at least one vehicle',
      'Vehicle must be registered & insured',
      'Must be 21 years or older',
      'Smartphone with data plan',
    ],
  },
};

const DriverApplicationPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [existingApplication, setExistingApplication] = useState<DriverApplication | null>(null);
  const [checkingApplication, setCheckingApplication] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedDriverType, setSelectedDriverType] = useState<DriverType | null>(null);

  const isAdminUser = FEATURE_FLAGS.ADMIN_EMAILS.includes(user?.email || '');

  const [formData, setFormData] = useState({
    fullName: '',
    email: user?.email || '',
    phoneNumber: '',
    licenseNumber: '',
    vehicleType: '',
    vehicleColor: '',
    vehicleMake: '',
    numberPlate: '',
  });

  const [makeSearch, setMakeSearch] = useState('');
  const [colorSearch, setColorSearch] = useState('');
  const [showMakeDropdown, setShowMakeDropdown] = useState(false);
  const [showColorDropdown, setShowColorDropdown] = useState(false);

  const filteredMakes = VEHICLE_MAKES.filter(m => m.toLowerCase().includes(makeSearch.toLowerCase()));
  const filteredColors = VEHICLE_COLORS.filter(c => c.toLowerCase().includes(colorSearch.toLowerCase()));

  useEffect(() => {
    checkExistingApplication();
  }, [user]);

  useEffect(() => {
    if (user?.email) {
      setFormData((prev) => ({ ...prev, email: user.email || '' }));
    }
  }, [user]);

  const checkExistingApplication = async () => {
    if (!user) {
      setCheckingApplication(false);
      return;
    }

    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (profile?.role === 'driver') {
        const { isRideDriverEmail } = await import('@/config/features');
        if (isRideDriverEmail(user.email)) {
          navigate('/ride-driver-dashboard');
        } else {
          navigate('/driver-dashboard');
        }
        return;
      }

      const { data, error } = await supabase
        .from('driver_applications')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking application:', error);
      } else if (data) {
        setExistingApplication(data as DriverApplication);
      }
    } catch (error) {
      console.error('Error checking application:', error);
    } finally {
      setCheckingApplication(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !selectedDriverType) return;

    const isRide = selectedDriverType === 'ride';
    if (!formData.fullName || !formData.phoneNumber || !formData.licenseNumber || !formData.vehicleType || (isRide && (!formData.vehicleColor || !formData.vehicleMake || !formData.numberPlate))) {
      toast({
        title: 'All fields required',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const insertData: any = {
        user_id: user.id,
        full_name: formData.fullName,
        email: formData.email,
        phone_number: formData.phoneNumber,
        license_number: formData.licenseNumber,
        vehicle_type: formData.vehicleType,
        driver_type: selectedDriverType,
        status: 'pending',
      };
      if (isRide) {
        insertData.vehicle_color = formData.vehicleColor;
        insertData.vehicle_make = formData.vehicleMake;
        insertData.vehicle_plate = formData.numberPlate;
      }
      const { error } = await supabase.from('driver_applications').insert(insertData);

      if (error) {
        if (error.code === '23505') {
          toast({
            title: 'Application Already Exists',
            description: 'You have already submitted a driver application.',
            variant: 'destructive',
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: 'Application Submitted',
          description: "Your application has been submitted successfully. We'll review it within 24-48 hours.",
        });
        checkExistingApplication();
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit application. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (checkingApplication) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (existingApplication) {
    const driverTypeLabel = DRIVER_TYPE_CONFIG[(existingApplication.driver_type as DriverType) || 'delivery']?.label || 'Driver';

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200 px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-6 w-6" />
            </Button>
            <h1 className="text-xl font-semibold">Application Status</h1>
          </div>
        </div>

        <div className="px-4 py-6 space-y-6">
          <Card>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                {existingApplication.status === 'pending' && <Clock className="h-16 w-16 text-yellow-500" />}
                {existingApplication.status === 'rejected' && <XCircle className="h-16 w-16 text-red-500" />}
              </div>
              <CardTitle className="text-2xl">
                Application {existingApplication.status === 'pending' ? 'Under Review' : 'Rejected'}
              </CardTitle>
              <CardDescription>
                {existingApplication.status === 'pending' &&
                  "Your application is being reviewed by our team. We'll notify you once a decision is made."}
                {existingApplication.status === 'rejected' &&
                  'Unfortunately, your application was not approved. You can contact support for more information.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Application Type</label>
                  <p className="mt-1 text-gray-900 font-semibold">{driverTypeLabel}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Full Name</label>
                  <p className="mt-1 text-gray-900">{existingApplication.full_name || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Email</label>
                  <p className="mt-1 text-gray-900">{existingApplication.email || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Phone Number</label>
                  <p className="mt-1 text-gray-900">{existingApplication.phone_number}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">License Number</label>
                  <p className="mt-1 text-gray-900">{existingApplication.license_number || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Vehicle Type</label>
                  <p className="mt-1 text-gray-900">{existingApplication.vehicle_type || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Application Date</label>
                  <p className="mt-1 text-gray-900">{new Date(existingApplication.created_at).toLocaleDateString()}</p>
                </div>
                {existingApplication.rejection_reason && (
                  <div>
                    <label className="text-sm font-medium text-red-600">Rejection Reason</label>
                    <p className="mt-1 text-red-900">{existingApplication.rejection_reason}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Driver type selection screen
  if (!selectedDriverType) {
    // Filter available types based on feature flags
    const availableTypes: DriverType[] = isAdminUser
      ? ['delivery', 'ride', 'rental']
      : ['delivery'];

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200 px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-6 w-6" />
            </Button>
            <h1 className="text-xl font-semibold">Driver Application</h1>
          </div>
        </div>

        <div className="px-4 py-6 space-y-6">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Join Our Team</CardTitle>
              <CardDescription>Choose the type of driver you want to be with GAMBGO</CardDescription>
            </CardHeader>
          </Card>

          <div className="space-y-4">
            {availableTypes.map((type) => {
              const config = DRIVER_TYPE_CONFIG[type];
              return (
                <Card
                  key={type}
                  className="cursor-pointer hover:border-red-300 hover:shadow-md transition-all active:scale-[0.98]"
                  onClick={() => setSelectedDriverType(type)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-red-50 flex items-center justify-center text-red-600 flex-shrink-0">
                        {config.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{config.label}</h3>
                        <p className="text-sm text-gray-600">{config.description}</p>
                      </div>
                      <ArrowLeft className="h-5 w-5 text-gray-400 rotate-180" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  const currentConfig = DRIVER_TYPE_CONFIG[selectedDriverType];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setSelectedDriverType(null)}>
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <h1 className="text-xl font-semibold">{currentConfig.label} Application</h1>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6">
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center text-red-600">
                {currentConfig.icon}
              </div>
            </div>
            <CardTitle className="text-2xl">Join Our Team</CardTitle>
            <CardDescription>Apply to become a GAMBGO {currentConfig.label}</CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Requirements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {currentConfig.requirements.map((requirement, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">{requirement}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{currentConfig.label} Application Form</CardTitle>
            <CardDescription>Fill out the form below to apply</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input id="email" type="email" value={formData.email} disabled className="bg-gray-100" />
                <p className="text-sm text-gray-600 mt-1">This is the email you signed up with</p>
              </div>

              <div>
                <Label htmlFor="phoneNumber">Phone Number *</Label>
                <Input
                  id="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  placeholder="Enter your phone number"
                  required
                />
              </div>

              <div>
                <Label htmlFor="licenseNumber">Driver's License Number *</Label>
                <Input
                  id="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
                  placeholder="Enter your driver's license number"
                  required
                />
              </div>

              <div>
                <Label htmlFor="vehicleType">{selectedDriverType === 'ride' ? 'Ride Category *' : 'Vehicle Type *'}</Label>
                <Select onValueChange={(value) => handleInputChange('vehicleType', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder={selectedDriverType === 'ride' ? 'Select ride category' : 'Select your vehicle type'} />
                  </SelectTrigger>
                  <SelectContent>
                    {currentConfig.vehicleOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedDriverType === 'ride' && (
                <>
                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Vehicle Information</p>
                  </div>

                  <div className="relative">
                    <Label htmlFor="vehicleMake">Vehicle Make *</Label>
                    <Input
                      id="vehicleMake"
                      value={makeSearch}
                      onChange={(e) => {
                        setMakeSearch(e.target.value);
                        setShowMakeDropdown(true);
                        if (!e.target.value) handleInputChange('vehicleMake', '');
                      }}
                      onFocus={() => setShowMakeDropdown(true)}
                      onBlur={() => setTimeout(() => setShowMakeDropdown(false), 200)}
                      placeholder="Search vehicle make (e.g. Toyota, BMW)"
                      autoComplete="off"
                    />
                    {showMakeDropdown && makeSearch && filteredMakes.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                        {filteredMakes.map((make) => (
                          <button
                            key={make}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              handleInputChange('vehicleMake', make);
                              setMakeSearch(make);
                              setShowMakeDropdown(false);
                            }}
                          >
                            {make}
                          </button>
                        ))}
                      </div>
                    )}
                    {showMakeDropdown && makeSearch && filteredMakes.length === 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 text-sm text-gray-500">
                        No results found
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <Label htmlFor="vehicleColor">Vehicle Color *</Label>
                    <Input
                      id="vehicleColor"
                      value={colorSearch}
                      onChange={(e) => {
                        setColorSearch(e.target.value);
                        setShowColorDropdown(true);
                        if (!e.target.value) handleInputChange('vehicleColor', '');
                      }}
                      onFocus={() => setShowColorDropdown(true)}
                      onBlur={() => setTimeout(() => setShowColorDropdown(false), 200)}
                      placeholder="Search color (e.g. Black, Silver)"
                      autoComplete="off"
                    />
                    {showColorDropdown && colorSearch && filteredColors.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                        {filteredColors.map((color) => (
                          <button
                            key={color}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors flex items-center gap-2"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              handleInputChange('vehicleColor', color);
                              setColorSearch(color);
                              setShowColorDropdown(false);
                            }}
                          >
                            {color}
                          </button>
                        ))}
                      </div>
                    )}
                    {showColorDropdown && colorSearch && filteredColors.length === 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 text-sm text-gray-500">
                        No results found
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="numberPlate">Number Plate *</Label>
                    <Input
                      id="numberPlate"
                      value={formData.numberPlate}
                      onChange={(e) => handleInputChange('numberPlate', e.target.value.toUpperCase())}
                      placeholder="Enter your number plate"
                      required
                    />
                  </div>
                </>
              )}

              <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Application'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="text-center text-sm text-gray-600">
          <p>Our team will contact you within 24-48 hours to review your application.</p>
        </div>
      </div>
    </div>
  );
};

export default DriverApplicationPage;
